import { useState, useMemo } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { MODEL_TYPES } from '../../utils/constants';

const baseCardinalityOptions = [
  {
    value: '1:1',
    label: 'One-to-One (1:1)',
    description: 'Each record in one table relates to exactly one record in another',
    example: 'Person → Passport',
    disabled: false
  },
  {
    value: '1:N',
    label: 'One-to-Many (1:N)',
    description: 'Each record in one table can relate to multiple records in another',
    example: 'Customer → Orders',
    disabled: false
  },
  {
    value: 'N:1',
    label: 'Many-to-One (N:1)',
    description: 'Multiple records in one table relate to one record in another',
    example: 'Orders → Customer',
    disabled: true
  },
  {
    value: 'N:N',
    label: 'Many-to-Many (N:N)',
    description: 'Records in both tables can relate to multiple records in the other',
    example: 'Students ↔ Courses',
    disabled: false
  }
];

const CardinalityModal = ({ onClose, onConfirm, sourceNode, targetNode, modelType }) => {
  // Adjust cardinality options based on model type
  const cardinalityOptions = useMemo(() => {
    if (modelType === MODEL_TYPES.ANALYTICAL) {
      // For analytical models: only allow 1:N and N:N
      return baseCardinalityOptions.map(option => ({
        ...option,
        disabled: option.value === '1:1' || option.value === 'N:1'
      }));
    }
    // For other models, use base options
    return baseCardinalityOptions;
  }, [modelType]);
  // Set default cardinality based on model type and available options
  const defaultCardinality = useMemo(() => {
    const validOptions = cardinalityOptions.filter(opt => !opt.disabled);
    return validOptions.length > 0 ? validOptions[0].value : '1:N';
  }, [cardinalityOptions]);

  const [selectedCardinality, setSelectedCardinality] = useState(defaultCardinality);
  const [label, setLabel] = useState('');
  const [fkAlias, setFkAlias] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(selectedCardinality, label, fkAlias);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Define Relationship Cardinality
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              <span className="font-medium text-blue-600 dark:text-blue-400">{sourceNode?.data?.name}</span>
              {' → '}
              <span className="font-medium text-blue-600 dark:text-blue-400">{targetNode?.data?.name}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              ⚠️ <strong>Important:</strong> Cardinality cannot be changed after creation. 
              Choose carefully based on your data model requirements.
            </p>
          </div>

          {/* Cardinality Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Select Cardinality Type *
            </label>
            <div className="space-y-2">
              {cardinalityOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start p-4 border-2 rounded-lg transition-all ${
                    option.disabled
                      ? 'opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
                      : selectedCardinality === option.value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-pointer'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer'
                  }`}
                >
                  <input
                    type="radio"
                    name="cardinality"
                    value={option.value}
                    checked={selectedCardinality === option.value}
                    onChange={(e) => setSelectedCardinality(e.target.value)}
                    disabled={option.disabled}
                    className="mt-1 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-semibold ${option.disabled ? 'text-gray-400 dark:text-gray-600' : 'text-gray-900 dark:text-white'}`}>
                        {option.label}
                      </span>
                      {option.disabled && (
                        <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                          Disabled
                        </span>
                      )}
                    </div>
                    <p className={`text-sm mt-1 ${option.disabled ? 'text-gray-400 dark:text-gray-600' : 'text-gray-600 dark:text-gray-400'}`}>
                      {option.description}
                    </p>
                    <p className={`text-xs mt-1 italic ${option.disabled ? 'text-gray-400 dark:text-gray-600' : 'text-gray-500 dark:text-gray-500'}`}>
                      Example: {option.example}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Label Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Relationship Label (Optional)
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="e.g., has, belongs to, contains..."
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              This label can be edited later in the properties panel
            </p>
          </div>

          {selectedCardinality !== 'N:N' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                FK field alias (optional)
              </label>
              <input
                type="text"
                value={fkAlias}
                onChange={(e) => setFkAlias(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g. parent, owner, ref"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                If set, shown in black italic to the left of the source entity name in the target entity&apos;s FK fields
              </p>
            </div>
          )}
        </form>

        {/* Actions */}
        <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            Create Relationship
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CardinalityModal;
